var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { action, computed, makeObservable, observable, runInAction, } from 'mobx';
import TimeUtils from '@business/utils/time';
import { OrganizationService } from '@data/api/services/organization';
import { changeLanguage } from '@data/other/i18n';
import { createColorScale, createContrastColorScale, } from '@theme/foundations/colors';
/** The property's amenity codes — legacy amenities carry none and are skipped. */
const getAmenityCodes = (property) => property.amenities
    .map((amenity) => { var _a, _b; return (_b = (_a = amenity.code) === null || _a === void 0 ? void 0 : _a.trim()) !== null && _b !== void 0 ? _b : ''; })
    .filter(Boolean);
export default class OrganizationStore {
    constructor(organizationId) {
        Object.defineProperty(this, "amenityFilter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "availabilityMap", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        Object.defineProperty(this, "checkinDate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: undefined
        });
        Object.defineProperty(this, "checkoutDate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: undefined
        });
        Object.defineProperty(this, "currency", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'usd'
        });
        Object.defineProperty(this, "customColors", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: undefined
        });
        Object.defineProperty(this, "expandedPropertyId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: undefined
        });
        Object.defineProperty(this, "isLoading", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isLoadingSummary", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isStickySearchBarVisible", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "language", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'en'
        });
        Object.defineProperty(this, "locationFilter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: undefined
        });
        Object.defineProperty(this, "organizationSummary", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: undefined
        });
        Object.defineProperty(this, "propertyDetails", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "summaryFailed", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "organizationId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // Identifies the latest detail request so a slower earlier one cannot
        // overwrite it. Switching language twice in quick succession otherwise
        // leaves the copy of whichever response happens to land last.
        // eslint-disable-next-line @typescript-eslint/member-ordering
        Object.defineProperty(this, "summaryRequestId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        // Same guard for availability: a search and a history navigation can
        // overlap, and every response replaces the whole map, so a slower earlier
        // one would show vacancy and prices for the wrong stay.
        // eslint-disable-next-line @typescript-eslint/member-ordering
        Object.defineProperty(this, "availabilityRequestId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        this.organizationId = organizationId;
        makeObservable(this);
    }
    /**
     * All property details filtered by location and amenities, with availability
     * merged in, available properties first — a property with no vacancy sinks
     * to the end rather than being removed, so a search still shows what exists.
     * Before availability has loaded every property defaults to available, so
     * nothing reorders until real vacancy data is in.
     */
    get properties() {
        let filtered = this.propertyDetails;
        if (this.locationFilter && this.locationFilter !== 'all') {
            const cityLower = this.locationFilter.toLowerCase();
            filtered = filtered.filter((p) => p.hotelAddress.city.toLowerCase() === cityLower);
        }
        // A property has to offer every selected amenity, not just one of them —
        // the same AND matching the property page's room filter uses.
        if (this.amenityFilter.length > 0) {
            filtered = filtered.filter((p) => {
                const codes = new Set(getAmenityCodes(p));
                return this.amenityFilter.every((code) => codes.has(code));
            });
        }
        return filtered
            .map((detail) => {
            var _a, _b, _c;
            const availability = this.availabilityMap.get(detail.id);
            return Object.assign(Object.assign({}, detail), { hasVacancy: (_a = availability === null || availability === void 0 ? void 0 : availability.hasVacancy) !== null && _a !== void 0 ? _a : true, minNightlyRate: (_b = availability === null || availability === void 0 ? void 0 : availability.minNightlyRate) !== null && _b !== void 0 ? _b : null, minTotalPrice: (_c = availability === null || availability === void 0 ? void 0 : availability.minTotalPrice) !== null && _c !== void 0 ? _c : null, 
                // The rate is quoted in the search currency, not the property's own
                // native currencyCode above — those can differ (e.g. a EUR property
                // searched in USD). Falls back when there is no rate to quote (no
                // vacancy, or availability hasn't loaded yet), where availability's
                // currencyCode is '' rather than absent.
                rateCurrencyCode: (availability === null || availability === void 0 ? void 0 : availability.currencyCode) || detail.currencyCode });
        })
            .sort((a, b) => Number(b.hasVacancy) - Number(a.hasVacancy));
    }
    /**
     * Every distinct amenity offered somewhere in the organization, as the filter
     * options. An amenity counts as top when at least one property marks it so.
     */
    get amenityOptions() {
        var _a;
        const byCode = new Map();
        for (const property of this.propertyDetails) {
            for (const amenity of property.amenities) {
                const code = (_a = amenity.code) === null || _a === void 0 ? void 0 : _a.trim();
                if (!code) {
                    continue;
                }
                const known = byCode.get(code);
                if (known) {
                    known.isTop = known.isTop || amenity.isTop;
                    continue;
                }
                byCode.set(code, {
                    category: amenity.categoryCode,
                    code,
                    isTop: amenity.isTop,
                    name: amenity.name,
                });
            }
        }
        return [...byCode.values()];
    }
    /**
     * Whether the amenities filter has anything to offer. Only coded (v2)
     * amenities can be filtered on: legacy properties expose untyped feature
     * names that cannot back a stable code, so an organization carrying none
     * gets no filter.
     */
    get isAmenityFilterAvailable() {
        return this.propertyDetails.some((property) => getAmenityCodes(property).length > 0);
    }
    get uniqueLocations() {
        const locations = this.propertyDetails.map((p) => p.hotelAddress.city);
        return [...new Set(locations)].sort();
    }
    /**
     * Brand colors expanded into Chakra theme scales, fed to `BaseProviders`.
     * Mirrors the hosted `Settings` model: the organization `primary` hex becomes
     * the full `primary` scale, and `primaryContrast` is derived from its 600
     * shade so button/calendar text stays readable. Returns `undefined` (default
     * theme) until organization summary with custom colors has loaded.
     */
    get themeColors() {
        if (!this.customColors) {
            return undefined;
        }
        const primary = createColorScale(this.customColors.primary);
        return {
            primary,
            primaryContrast: createContrastColorScale(primary[600]),
        };
    }
    /** Fetches organization detail in the current language. */
    loadOrganizationSummary() {
        return __awaiter(this, void 0, void 0, function* () {
            const requestId = ++this.summaryRequestId;
            this.isLoadingSummary = true;
            this.summaryFailed = false;
            try {
                const detail = yield OrganizationService.getDetail(this.organizationId, this.language);
                if (requestId !== this.summaryRequestId) {
                    return;
                }
                runInAction(() => {
                    this.organizationSummary = {
                        customColors: detail.customColors,
                        organizationId: detail.organizationId,
                        organizationName: detail.organizationName,
                        organizationSlug: detail.organizationSlug,
                        properties: detail.properties,
                    };
                    this.customColors = detail.customColors;
                    this.propertyDetails = detail.properties;
                    this.isLoadingSummary = false;
                    this.summaryFailed = false;
                });
            }
            catch (error) {
                if (requestId !== this.summaryRequestId) {
                    return;
                }
                runInAction(() => {
                    this.isLoadingSummary = false;
                    this.summaryFailed = true;
                });
                console.error('Failed to load organization summary:', error);
            }
        });
    }
    loadAvailability() {
        return __awaiter(this, void 0, void 0, function* () {
            // Check-in and check-out are required together — the backend 400s given
            // only one or neither. Skip rather than fire a request guaranteed to
            // fail (e.g. mid-selection, with one date cleared and the other not).
            // Bumping the request id invalidates any earlier request still in
            // flight, so it can't resolve later and repopulate stale vacancy/prices;
            // clearing the map drops whatever the previous complete stay showed.
            if (!this.checkinDate || !this.checkoutDate) {
                ++this.availabilityRequestId;
                this.availabilityMap.clear();
                this.isLoading = false;
                return;
            }
            const requestId = ++this.availabilityRequestId;
            this.isLoading = true;
            try {
                const params = {
                    checkIn: TimeUtils.format(this.checkinDate, 'yyyy-MM-dd'),
                    checkOut: TimeUtils.format(this.checkoutDate, 'yyyy-MM-dd'),
                    currency: this.currency,
                };
                const response = yield OrganizationService.getAvailability(this.organizationId, params);
                if (requestId !== this.availabilityRequestId) {
                    return;
                }
                runInAction(() => {
                    this.availabilityMap.clear();
                    for (const prop of response.properties) {
                        this.availabilityMap.set(prop.id, {
                            currencyCode: prop.currencyCode,
                            hasVacancy: prop.hasVacancy,
                            minNightlyRate: prop.minNightlyRate,
                            minTotalPrice: prop.minTotalPrice,
                        });
                    }
                    this.isLoading = false;
                });
            }
            catch (error) {
                if (requestId !== this.availabilityRequestId) {
                    return;
                }
                runInAction(() => {
                    this.isLoading = false;
                });
                console.error('Failed to load availability:', error);
            }
        });
    }
    setAmenityFilter(codes) {
        this.amenityFilter = codes;
    }
    setCurrency(currency) {
        this.currency = currency;
    }
    setDates(checkin, checkout) {
        this.checkinDate = checkin;
        this.checkoutDate = checkout;
    }
    setLanguage(language) {
        return __awaiter(this, void 0, void 0, function* () {
            this.language = language;
            yield changeLanguage(language);
            // Descriptions are localized server-side, so the detail payload is stale.
            yield this.loadOrganizationSummary();
        });
    }
    setLocationFilter(location) {
        this.locationFilter = location;
    }
    setStickySearchBarVisible(isVisible) {
        this.isStickySearchBarVisible = isVisible;
    }
    toggleDetails(propertyId) {
        this.expandedPropertyId =
            this.expandedPropertyId === propertyId ? undefined : propertyId;
    }
}
__decorate([
    observable,
    __metadata("design:type", Array)
], OrganizationStore.prototype, "amenityFilter", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], OrganizationStore.prototype, "availabilityMap", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], OrganizationStore.prototype, "checkinDate", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], OrganizationStore.prototype, "checkoutDate", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], OrganizationStore.prototype, "currency", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], OrganizationStore.prototype, "customColors", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], OrganizationStore.prototype, "expandedPropertyId", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], OrganizationStore.prototype, "isLoading", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], OrganizationStore.prototype, "isLoadingSummary", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], OrganizationStore.prototype, "isStickySearchBarVisible", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], OrganizationStore.prototype, "language", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], OrganizationStore.prototype, "locationFilter", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], OrganizationStore.prototype, "organizationSummary", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], OrganizationStore.prototype, "propertyDetails", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], OrganizationStore.prototype, "summaryFailed", void 0);
__decorate([
    computed,
    __metadata("design:type", Array),
    __metadata("design:paramtypes", [])
], OrganizationStore.prototype, "properties", null);
__decorate([
    computed,
    __metadata("design:type", Array),
    __metadata("design:paramtypes", [])
], OrganizationStore.prototype, "amenityOptions", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], OrganizationStore.prototype, "isAmenityFilterAvailable", null);
__decorate([
    computed,
    __metadata("design:type", Array),
    __metadata("design:paramtypes", [])
], OrganizationStore.prototype, "uniqueLocations", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], OrganizationStore.prototype, "themeColors", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], OrganizationStore.prototype, "loadOrganizationSummary", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], OrganizationStore.prototype, "loadAvailability", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Array]),
    __metadata("design:returntype", void 0)
], OrganizationStore.prototype, "setAmenityFilter", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", void 0)
], OrganizationStore.prototype, "setCurrency", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, Object]),
    __metadata("design:returntype", void 0)
], OrganizationStore.prototype, "setDates", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", Promise)
], OrganizationStore.prototype, "setLanguage", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], OrganizationStore.prototype, "setLocationFilter", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Boolean]),
    __metadata("design:returntype", void 0)
], OrganizationStore.prototype, "setStickySearchBarVisible", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Number]),
    __metadata("design:returntype", void 0)
], OrganizationStore.prototype, "toggleDetails", null);
